import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ItemsListRowComponent } from '@fi-app/components/items-list/list-row/list-row.component';
import { ObjectTypes } from '@fi-shared/types/object-type';
import { AppTextService } from '@fi-shared/app-text';
import { RollupContentComponent } from '@fi-shared/rollup/rollup-content.component';
import { isFunction } from '@fi-shared/utils/is-function';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { FiChatService } from '@fi-shared/fi-chat/fi-chat.service';
import { createMap } from '@fi-shared/utils/create-map';
import { UserService } from '@fi-app/user/user.service';
import { BusinessUnitService } from '@fi-app/business-unit/business-unit.service';
import { FiFormatDateService } from '@fi-shared/fi-format-date/fi-format-date.service';

interface IMediaBankUsesItem {
    MediaBankFileId: number;
    ObjectName: string;
    ObjectCreatedByUserName: string;
    ObjectCreatedOn: string; //ISO 8601 date
    ObjectId: number;
    ObjectType: any;
    ForumId: number;
    CommentInfo: {
        CommentId: number;
        ObjectName: ((item: IMediaBankUsesItem) => string) | string;
        ObjectCreatedByUserName: string;
        ObjectId: number;
        ObjectType: any;
    };
}

interface IMediaBankUsesGroup {
    ObjectType;
    Title: string;
    StateName: any;
    StateParams: (item: IMediaBankUsesItem) => any;
    UsesItems?: IMediaBankUsesItem[];
}

@Component({
    selector: 'fi-media-bank-confirm-delete-view-row',
    templateUrl: './media-bank-confirm-delete-view-row.component.html',
    styleUrls: ['./media-bank-confirm-delete-view-row.component.scss'],
    standalone: false,
})
export class MediaBankConfirmDeleteViewRowComponent implements OnInit, OnDestroy {
    usesGroups: IMediaBankUsesGroup[];
    userGroupsMap: { [key: string]: IMediaBankUsesGroup };

    @ViewChild('rollupContent', { static: false }) rollupContent: RollupContentComponent;

    constructor(
        public row: ItemsListRowComponent<any>,
        private appTextService: AppTextService,
        private fiChatService: FiChatService,
        private userService: UserService,
        private businessUnitService: BusinessUnitService,
        private fiFormatDateService: FiFormatDateService,
    ) {}

    get dataItem(): {
        UsesItems: IMediaBankUsesItem[];
    } {
        return this.row.data;
    }

    ngOnInit() {
        this.usesGroups = [
            {
                ObjectType: ObjectTypes.Document,
                Title: this.appTextService.get('common.page'),
                StateName: FiStateNames.Document.View,
                StateParams: (item) => ({ Id: item.ObjectId }),
            },
            {
                ObjectType: ObjectTypes.Template,
                Title: this.appTextService.get('common.page_template'),
                StateName: FiStateNames.Document.Template.View,
                StateParams: (item) => ({ Id: item.ObjectId }),
            },
            {
                ObjectType: ObjectTypes.CheckList,
                Title: this.appTextService.get('common.checklist'),
                StateName: FiStateNames.Checklist.View,
                StateParams: (item) => ({ Id: item.ObjectId }),
            },
            {
                ObjectType: ObjectTypes.Post,
                Title: this.appTextService.get('common.post'),
                StateName: FiStateNames.Home.Post._name,
                StateParams: (item) => ({ postId: item.ObjectId, objectType: item.ObjectType }),
            },
            {
                ObjectType: ObjectTypes.ForumPost,
                Title: this.appTextService.get('common.forum_post'),
                StateName: FiStateNames.Forum.Post.View,
                StateParams: (item) => ({ id: item.ObjectId, forumId: item.ForumId }),
            },
            {
                ObjectType: ObjectTypes.User,
                Title: this.appTextService.get('common.UserProfile'),
                StateName: undefined,
                StateParams: () => undefined,
            },
            {
                ObjectType: ObjectTypes.BusinessUnit,
                Title: this.appTextService.get('common.unit_profile'),
                StateName: undefined,
                StateParams: () => undefined,
            },
            {
                ObjectType: ObjectTypes.Task,
                Title: this.appTextService.get('task.task'),
                StateName: FiStateNames.Task.Details,
                StateParams: (item) => ({ id: item.ObjectId }),
            },
            {
                ObjectType: ObjectTypes.ChatConversation,
                Title: this.appTextService.get('chat.chat'),
                StateName: undefined,
                StateParams: (item) => undefined,
            },
            {
                ObjectType: ObjectTypes.Widget,
                Title: this.appTextService.get('common.startpage_widget'),
                StateName: FiStateNames.Home._name,
                StateParams: (item) => ({}),
            },
            {
                ObjectType: ObjectTypes.WidgetAdministrate,
                Title: this.appTextService.get('common.startpage_widget'),
                StateName: FiStateNames.Home._name,
                StateParams: (item) => ({}),
            },
            {
                ObjectType: ObjectTypes.Comment,
                Title: this.appTextService.get('common.comment'),
                StateName: (item) => {
                    switch (item.CommentInfo.ObjectType) {
                        case ObjectTypes.Post:
                            return FiStateNames.Home.Post.Comment;
                        case ObjectTypes.ForumPost:
                            return FiStateNames.Forum.Post.View;
                        case ObjectTypes.Document:
                            return FiStateNames.Document.View;
                        case ObjectTypes.MediaBank:
                            return FiStateNames.MediaBank.View;
                        case ObjectTypes.Task:
                            return FiStateNames.Task.Details;
                    }
                },
                StateParams: (item) => {
                    switch (item.CommentInfo.ObjectType) {
                        case ObjectTypes.Post:
                            return {
                                postId: item.CommentInfo.ObjectId,
                                objectType: item.CommentInfo.ObjectType,
                                commentId: item.ObjectId,
                            };
                        case ObjectTypes.ForumPost:
                            return {
                                id: item.CommentInfo.ObjectId,
                                forumId: item.ForumId,
                                commentId: item.ObjectId,
                            };
                        case ObjectTypes.Document:
                            return { Id: item.CommentInfo.ObjectId };
                        case ObjectTypes.MediaBank:
                            return { Id: item.CommentInfo.ObjectId };
                        case ObjectTypes.Task:
                            return { id: item.CommentInfo.ObjectId };
                    }
                },
            },
        ];

        this.userGroupsMap = createMap(this.usesGroups, 'ObjectType');
        this.dataItem.UsesItems.forEach((item) => {
            if (!this.userGroupsMap[item.ObjectType]) return;

            if (!this.userGroupsMap[item.ObjectType].UsesItems) this.userGroupsMap[item.ObjectType].UsesItems = [];

            if (item.ObjectType == ObjectTypes.Widget && item.ObjectName) {
                const name = JSON.parse(item.ObjectName).Name;
                item.ObjectName =
                    name === 'AdvancedText' ? this.appTextService.get('widget.default_advanced_text_name') : name;
            }

            this.userGroupsMap[item.ObjectType].UsesItems.push(item);
        });

        this._initEvents();
    }

    getItemStateName(item: IMediaBankUsesItem) {
        const stateName = this.userGroupsMap[item.ObjectType].StateName;
        return isFunction(stateName) ? stateName(item) : stateName;
    }

    getItemStateParams(item: IMediaBankUsesItem) {
        return this.userGroupsMap[item.ObjectType].StateParams(item);
    }

    private isPost(ObjectType) {
        return ObjectType === ObjectTypes.Post || ObjectType === ObjectTypes.ForumPost;
    }

    private isPostOrCommentToPost(item: IMediaBankUsesItem) {
        return (
            this.isPost(item.ObjectType) ||
            (item.ObjectType === ObjectTypes.Comment && this.isPost(item.CommentInfo.ObjectType))
        );
    }

    getItemText(item: IMediaBankUsesItem) {
        if (this.isPostOrCommentToPost(item))
            return this.fiFormatDateService.formatDate(item.ObjectCreatedOn, 'date-time');

        if (item.ObjectType === ObjectTypes.Comment && !this.isPost(item.CommentInfo.ObjectType))
            return item.CommentInfo.ObjectName;

        return item.ObjectName;
    }

    getItemTitle(item: IMediaBankUsesItem) {
        if (this.isPost(item.ObjectType)) return item.ObjectCreatedByUserName;

        if (item.ObjectType === ObjectTypes.Comment && this.isPost(item.CommentInfo.ObjectType))
            return item.CommentInfo.ObjectCreatedByUserName;

        return false;
    }

    isShowDateIcon(item) {
        return this.isPostOrCommentToPost(item);
    }

    itemClick(item: IMediaBankUsesItem, group, $event) {
        if (item.ObjectType === ObjectTypes.ChatConversation) {
            $event.preventDefault();
            this.fiChatService.triggerOpenConversationByIdWithAccessCheck(item.ObjectId);
        }
        if (item.ObjectType === ObjectTypes.User) {
            $event.preventDefault();
            this.userService.openUserViewDialog(item.ObjectId);
        }
        if (item.ObjectType === ObjectTypes.BusinessUnit) {
            this.businessUnitService.openBusinessUnitViewDialog(item.ObjectId);
        }
    }

    _initEvents() {
        this.row.events$.pipe(takeUntil(this.ngOnDestroy$)).subscribe((event) => {
            if (event.event === 'onShowUsesClick') {
                event.data ? this.rollupContent.openContent() : this.rollupContent.closeContent();
            }
        });
    }

    ngOnDestroy$ = new Subject<void>();

    ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
